<template>
  <div v-if="content || images" class="product-block" :class="additionalClass">
    <div class="product-block__inner">
      <div v-if="content" class="product-block__content" :class="{ 'product-block__content_opened': contentState }">
        <div class="product-block__col-holder" ref="productBlockColHolder" v-resize="getContentHeight">
          <div class="product-block__col">
            <product-content :content="content" />
          </div>
        </div>
        <div v-if="isContentCut" class="product-block__content-more-link">
          <span @click="toggleContent">
            {{ contentState ? langJson[lang].seeLess : langJson[lang].seeMore }}
          </span>
        </div>
      </div>
      <product-gallery v-if="images" :title="langJson[lang].gallery" :more="langJson[lang].seeAll" :resources="images" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';

  interface ContentItem {
    [key: string]: any;
  }

  interface VideoItem {
    [key: string]: any;
  }

  interface PlaceholderItem {
    [key: string]: any;
  }

  interface ProductItem {
    [key: string]: any;
  }

  interface NodeItem {
    [key: string]: any;
  }

  interface ImageItem {
    [key: string]: any;
  }

  const props = defineProps<{
    additionalClass?: string;
    content?: ContentItem;
    images?: ImageItem[];
    title?: string;
    video?: VideoItem;
    placeholder?: PlaceholderItem;
    items?: ProductItem[];
    nodes?: NodeItem[];
    // received from ProductBlock but not used in this block type
    publishedTo?: number | string;
  }>();

  const mainStore = useMainStore();
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);

  const contentHeight = ref(0);
  const maxContentHeight = ref(250);
  const contentState = ref(false);
  const productBlockColHolder = ref<HTMLElement | null>(null);
  let ro: ResizeObserver | null = null;
  let mo: MutationObserver | null = null;

  const isContentCut = computed(() => contentHeight.value > maxContentHeight.value);

  function getContentHeight() {
    const el = productBlockColHolder.value;
    if (el) {
      // Use scrollHeight to capture full content height beyond the clamp
      contentHeight.value = el.scrollHeight || el.clientHeight || 0;
    }
  }

  function toggleContent() {
    contentState.value = !contentState.value;
  }

  onMounted(async () => {
    await nextTick();
    getContentHeight();
    const el = productBlockColHolder.value;
    if (import.meta.client && el) {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => getContentHeight());
        ro.observe(el);
      }
      // Observe DOM mutations if content is injected dynamically
      if ('MutationObserver' in window) {
        mo = new MutationObserver(() => Promise.resolve().then(() => getContentHeight()));
        mo.observe(el, { childList: true, subtree: true, characterData: true });
      }
    }
  });

  onBeforeUnmount(() => {
    if (ro) {
      ro.disconnect();
      ro = null;
    }
    if (mo) {
      mo.disconnect();
      mo = null;
    }
  });

  // Recalculate on content changes
  watch(
    () => props.content,
    async () => {
      await nextTick();
      getContentHeight();
    },
    { deep: true }
  );
</script>
